import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronRightIcon, CheckIcon, MapPinIcon, CalendarIcon, UsersIcon } from 'lucide-react';
import { Container, Kicker } from '../components/ui/Primitives';
import { Button } from '../components/ui/Button';
import { Seo } from '../components/Seo';
import { getPosition, INTERNSHIP_TERMS, OPEN_POSITIONS } from '../data/careers';
import { breadcrumbSchema, canonical, SITE } from '../lib/seo';
import { NotFound } from './NotFound';
const DATE_POSTED = '2026-09-28';
const VALID_THROUGH = '2027-03-31T23:59:00+02:00';
export function CareerPosition() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const position = getPosition(slug ?? '');
  if (!position) return <NotFound data-id="element-2234" data-mp="lw72mdn08ymm" />;
  const path = `/careers/${position.slug}`;
  const others = OPEN_POSITIONS.filter(item => item.slug !== position.slug).slice(0, 3);
  const jobPosting = {
    '@type': 'JobPosting',
    '@id': `${canonical(path)}#job`,
    title: position.title,
    description: [`<p>${position.description}</p>`, `<p><strong>Responsibilities</strong></p><ul>${position.responsibilities.map(item => `<li>${item}</li>`).join('')}</ul>`, `<p><strong>What we are looking for</strong></p><ul>${[...position.requirements, ...position.bonus.map(item => `Bonus: ${item}`)].map(item => `<li>${item}</li>`).join('')}</ul>`, `<p>${INTERNSHIP_TERMS.duration}, ${INTERNSHIP_TERMS.fullTimeNote}. ${INTERNSHIP_TERMS.reporting}</p>`].join(''),
    datePosted: DATE_POSTED,
    validThrough: VALID_THROUGH,
    employmentType: 'INTERN',
    industry: 'Management consulting and government technology',
    occupationalCategory: position.team,
    hiringOrganization: {
      '@type': 'Organization',
      '@id': `${SITE.url}/#organisation`,
      name: SITE.name,
      sameAs: SITE.url,
      logo: SITE.shareImage
    },
    jobLocation: {
      '@type': 'Place',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Kigali',
        addressCountry: 'RW'
      }
    },
    directApply: true,
    url: canonical(path)
  };
  return <>
      <Seo title={`${position.title}, Kigali`} description={`${position.summary} A ${INTERNSHIP_TERMS.duration} internship in Kigali with ${position.team}, ${INTERNSHIP_TERMS.fullTimeNote}.`} path={path} jsonLd={[jobPosting, breadcrumbSchema([{
      name: 'Home',
      path: '/'
    }, {
      name: 'Careers',
      path: '/careers'
    }, {
      name: position.title,
      path
    }])]} data-id="element-2235" data-mp="1s93h867qvrwl" />

      {/* Hero */}
      <section className="surface-dark" data-id="element-2236" data-mp="2fz7lmfsrbvon">
        <Container className="py-12 sm:py-16" data-id="element-2237" data-mp="2fwbm4n19gyy3">
          <nav aria-label="Breadcrumb" className="mb-8" data-id="element-2238" data-mp="2kxhdohj6qg61">
            <ol className="flex flex-wrap items-center gap-2 text-small text-white/55" data-id="element-2239" data-mp="2kuv6ij07i2oc">
              <li data-id="element-2240" data-mp="lm7vhsqr3j34">
                <Link to="/" className="hover:text-white" data-id="element-2241" data-mp="qj81k3nlolu8">Home</Link>
              </li>
              <li aria-hidden="true" data-id="element-2242" data-mp="ql1bpsxf9o6z">
                <ChevronRightIcon className="h-3.5 w-3.5" data-id="element-2243" data-mp="izytcdqtyt6r" />
              </li>
              <li data-id="element-2244" data-mp="e98px61yt0ep">
                <Link to="/careers" className="hover:text-white" data-id="element-2245" data-mp="e8p4vgh0pvwg">Careers</Link>
              </li>
              <li aria-hidden="true" data-id="element-2246" data-mp="994a7i8vs9pj">
                <ChevronRightIcon className="h-3.5 w-3.5" data-id="element-2247" data-mp="9a5ixg2fck43" />
              </li>
              <li aria-current="page" className="truncate text-white/80" data-id="element-2248" data-mp="4c408w8wumt5">{position.title}</li>
            </ol>
          </nav>

          <Kicker data-id="element-2249" data-mp="11wgv7nze89gq">{position.team}</Kicker>
          <h1 className="mt-4 max-w-4xl font-display text-[30px] font-light leading-tight text-white sm:text-[44px]" data-id="element-2250" data-mp="wyylhvdjxq7o">
            {position.title}
          </h1>
          <ul className="mt-7 flex flex-wrap gap-x-7 gap-y-3 text-[15px] text-white/75" data-id="element-2251" data-mp="s2ghixq1pvwe">
            <li className="flex items-center gap-2" data-id="element-2252" data-mp="nyifof9c66zn">
              <MapPinIcon className="h-4 w-4 text-gold" aria-hidden="true" data-id="element-2253" data-mp="3cquslaxb09xz" />
              {position.location}
            </li>
            <li className="flex items-center gap-2" data-id="element-2254" data-mp="38ng03ks6i13n">
              <CalendarIcon className="h-4 w-4 text-gold" aria-hidden="true" data-id="element-2255" data-mp="10gkx6dhnbob5" />
              Internship, {INTERNSHIP_TERMS.duration}
            </li>
            <li className="flex items-center gap-2" data-id="element-2256" data-mp="158m55n7s6svd">
              <UsersIcon className="h-4 w-4 text-gold" aria-hidden="true" data-id="element-2257" data-mp="qinb9wn9e3lp" />
              {position.team}
            </li>
          </ul>
          {position.isOpen && <div className="mt-9" data-id="element-2258" data-mp="vevjsr6yayjw">
              <Button to={`/careers/apply?position=${position.slug}`} size="lg" shape="pill" data-id="element-2259" data-mp="1j6aogqm79ryb">
                Apply for this role
              </Button>
            </div>}
        </Container>
      </section>

      {/* Body */}
      <Container className="py-12 sm:py-16" data-id="element-2260" data-mp="1a5kxc07vwpqx">
        <div className="grid gap-12 lg:grid-cols-[1fr_340px] lg:gap-16" data-id="element-2261" data-mp="1e7fxh1e2wx34">
          <article className="max-w-prose" data-id="element-2262" data-mp="1e9sos55q8jsu">
            <h2 className="font-display text-[24px] text-ink" data-id="element-2263" data-mp="3uw3lnlyxlf5g">What you will do</h2>
            <p className="mt-4 text-[17px] leading-relaxed text-body" data-id="element-2264" data-mp="2sawq2xid8m5">{position.description}</p>

            <h2 className="mt-12 font-display text-[24px] text-ink" data-id="element-2265" data-mp="3re5vngiph5g5">Responsibilities</h2>
            <ul className="mt-5 space-y-3" data-id="element-2266" data-mp="3mfcpya9nuiqj">
              {position.responsibilities.map(item => <li key={item} className="flex gap-3 text-[16px] leading-relaxed text-body" data-id="element-2267" data-mp="56xten331eoc">
                  <CheckIcon className="mt-1.5 h-4 w-4 shrink-0 text-gold" aria-hidden="true" data-id="element-2268" data-mp="84pnmdll21d" />
                  {item}
                </li>)}
            </ul>

            <h2 className="mt-12 font-display text-[24px] text-ink" data-id="element-2269" data-mp="38lttouvys66j">What we are looking for</h2>
            <ul className="mt-5 space-y-3" data-id="element-2270" data-mp="33qo8jyadyvji">
              {position.requirements.map(item => <li key={item} className="flex gap-3 text-[16px] leading-relaxed text-body" data-id="element-2271" data-mp="3if2cydngas26">
                  <span className="mt-[11px] h-1.5 w-1.5 shrink-0 rounded-full bg-gold" aria-hidden="true" data-id="element-2272" data-mp="3dlpxat0r1vlk" />
                  {item}
                </li>)}
            </ul>

            {position.bonus.length > 0 && <>
                <h3 className="mt-8 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted" data-id="element-2273" data-mp="3an0wn5t3syro">
                  Not required, but welcome
                </h3>
                <ul className="mt-4 space-y-3" data-id="element-2274" data-mp="3elq7orqo6485">
                  {position.bonus.map(item => <li key={item} className="flex gap-3 text-[16px] leading-relaxed text-body" data-id="element-2275" data-mp="3jh5j7d17fo1e">
                      <span className="mt-[11px] h-1.5 w-1.5 shrink-0 rounded-full border border-gold" aria-hidden="true" data-id="element-2276" data-mp="3ofqhv80t5znq" />
                      {item}
                    </li>)}
                </ul>
              </>}
          </article>

          <aside className="lg:sticky lg:top-28 lg:self-start" data-id="element-2277" data-mp="30mqi9hvc1lax">
            <div className="rounded-md border border-line bg-panel p-6" data-id="element-2278" data-mp="35nxrzmh1cuk8">
              <h2 className="font-display text-[20px] text-ink" data-id="element-2279" data-mp="fy2q66l0cafs">The internship</h2>
              <dl className="mt-5 space-y-4 text-[15px]" data-id="element-2280" data-mp="g06wev6dplj8">
                <div data-id="element-2281" data-mp="kw46wkuynxo3">
                  <dt className="text-small text-muted" data-id="element-2282" data-mp="kvce1hsr0e1i">Location</dt>
                  <dd className="mt-1 text-ink" data-id="element-2283" data-mp="25e6dyns5qmfn">{position.location}</dd>
                </div>
                <div data-id="element-2284" data-mp="20fv5ohhigjzj">
                  <dt className="text-small text-muted" data-id="element-2285" data-mp="20gn1ntf0sjqc">Duration</dt>
                  <dd className="mt-1 text-ink" data-id="element-2286" data-mp="1vomv3i4g2sks">
                    {INTERNSHIP_TERMS.duration}, {INTERNSHIP_TERMS.fullTimeNote}
                  </dd>
                </div>
                <div data-id="element-2287" data-mp="1moqe82oaqy0a">
                  <dt className="text-small text-muted" data-id="element-2288" data-mp="1mp9mero5a814">Support</dt>
                  <dd className="mt-1 text-body" data-id="element-2289" data-mp="1mmdoh3carjco">{INTERNSHIP_TERMS.reporting}</dd>
                </div>
              </dl>
              {position.isOpen ? <Button to={`/careers/apply?position=${position.slug}`} className="mt-7 w-full" data-id="element-2290" data-mp="1crbxs32di97z">
                  Apply for this role
                </Button> : <p className="mt-7 text-[15px] text-muted" data-id="element-2291" data-mp="374tkjnuyf21y">This position is now closed.</p>}
              <p className="mt-4 text-small text-muted" data-id="element-2292" data-mp="326jtnpg9inm5">One form, no account. We reply within two weeks.</p>
            </div>
          </aside>
        </div>
      </Container>

      {others.length > 0 && <section aria-labelledby="other-roles" className="border-t border-line bg-mist" data-id="element-2293" data-mp="2fxwenf0qbnzn">
          <Container className="py-12 sm:py-16" data-id="element-2294" data-mp="226rn707im20e">
            <h2 id="other-roles" className="font-display text-[24px] text-ink" data-id="element-2295" data-mp="22600yfzt5gms">Other open roles</h2>
            <ul className="mt-6 divide-y divide-line border-y border-line" data-id="element-2296" data-mp="2671ssbreaqeq">
              {others.map(item => <li key={item.slug} data-id="element-2297" data-mp="1x8gdcph7zri6">
                  <Link to={`/careers/${item.slug}`} className="flex flex-col gap-1 py-5 transition-colors duration-150 hover:text-gold sm:flex-row sm:items-center sm:justify-between" data-id="element-2298" data-mp="1x9j2osyw9lvz">
                    <span className="font-display text-[19px] text-ink" data-id="element-2299" data-mp="1ncda1gamtmtn">{item.title}</span>
                    <span className="text-small text-muted" data-id="element-2300" data-mp="3dkvic9pu9w71">{item.team}</span>
                  </Link>
                </li>)}
            </ul>
          </Container>
        </section>}
    </>;
}